/**
 * sidebarCountsService.js — iter319
 *
 * Service partagé pour tous les compteurs de la sidebar admin.
 * Remplace 8-12 fetches concurrents par 1 seul GET /api/admin/sidebar-counts
 * avec cache in-memory + déduplication des appels concurrents.
 *
 * Composants consommateurs :
 *   - GolfModule.js (sidebar badges : taylor-made, compétitions, stages, …)
 *   - GolfClients.js (admin_notifications.by_client)
 *   - AdminControlCenterBell.js (control_center.{total,critical,warning,info})
 *
 * Mécanique :
 *   - Un seul appel réseau toutes les 60s (interval géré par GolfModule au mount).
 *   - Les autres composants s'abonnent via `subscribe(cb)` et reçoivent le même
 *     payload sans redéclencher de fetch.
 *   - `invalidate()` purge le cache local + côté serveur et refetch immédiat.
 *   - L'event legacy `refresh-badges` sur window continue de fonctionner.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const CACHE_TTL = 20 * 1000; // 20s client-side (le serveur a aussi un TTL de 20s)

let _cache = null;        // { ts, data }
let _inflight = null;     // Promise d'un fetch en cours (déduplication)
const _subscribers = new Set();

/** Ajoute un listener invoqué avec les data à chaque fetch réussi.
 * Retourne une fonction unsubscribe().
 */
export function subscribe(callback) {
  _subscribers.add(callback);
  // Si on a déjà des data en cache, les livrer immédiatement
  if (_cache?.data) {
    try { callback(_cache.data); } catch (_) { /* ignore */ }
  }
  return () => { _subscribers.delete(callback); };
}

function _notify(data) {
  _subscribers.forEach(cb => {
    try { cb(data); } catch (_) { /* ignore */ }
  });
}

/** Fetch les compteurs. Retourne la version cachée si elle a moins de 20s,
 * sinon lance un GET /api/admin/sidebar-counts.
 * @param {boolean} force si true, ignore le cache local.
 */
export async function fetchSidebarCounts(force = false) {
  const now = Date.now();
  if (!force && _cache && (now - _cache.ts) < CACHE_TTL) {
    return _cache.data;
  }
  if (_inflight) return _inflight;

  const token = localStorage.getItem('access_token');
  if (!token) return null;

  _inflight = axios
    .get(`${API_URL}/api/admin/sidebar-counts`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 15000,
    })
    .then(res => {
      _cache = { ts: Date.now(), data: res.data };
      _notify(res.data);
      return res.data;
    })
    .catch(err => {
      // 403 silencieux (ex: client portail qui tombe sur cette route par erreur)
      if (err?.response?.status === 403 || err?.response?.status === 401) {
        return null;
      }
      // eslint-disable-next-line no-console
      console.warn('[sidebarCounts] fetch error', err?.message || err);
      return _cache?.data || null;
    })
    .finally(() => { _inflight = null; });

  return _inflight;
}

/** Purge le cache local + serveur et refetch immédiat. */
export async function invalidateSidebarCounts() {
  _cache = null;
  const token = localStorage.getItem('access_token');
  if (token) {
    try {
      await axios.post(`${API_URL}/api/admin/sidebar-counts/invalidate`, null, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
    } catch (_) { /* non-bloquant */ }
  }
  return fetchSidebarCounts(true);
}

/** Dernière valeur connue, synchrone (null si rien chargé). */
export function getCachedSidebarCounts() {
  return _cache?.data || null;
}

// Legacy : écouter l'event `refresh-badges` existant pour déclencher un refetch.
if (typeof window !== 'undefined') {
  window.addEventListener('refresh-badges', () => { invalidateSidebarCounts(); });
}
